import { AddressTypes, BillingStatuses, ChargeStatus, ChartOfAccountCategory, MemberStatuses, OrganizationStatuses, PaymentStatus, PricingTiers, PropertyStatuses, UserInvitationStatuses, UserStatuses } from "../enums/data.enums";
import { BudgetListSortOptions, ChartOfAccountListSortOptions, CountryListSortOptions, ExpenseListSortOptions, FeatureFlagOverrideSortOptions, FeatureFlagSortOptions, MemberListSortOptions, OrganizationListSortOptions, PaymentHistoryListSortOptions, PropertyChargeListSortOptions, PropertyListSortOptions, PropertyChargeScheduleListSortOptions, SentEmailSortOptions, StateProvinceListSortOptions, UserListSortOptions } from "../enums/sort-options.enums" ;
import { BudgetsFilter } from "../filters/budgets.filter";
import { ChartOfAccountsFilter } from "../filters/chart-of-accounts.filter";
import { ExpenseFilter } from "../filters/expense.filter";
import { FeatureFlagOverridesFilter, FeatureFlagsFilter } from "../filters/feature-flags.filter";
import { SentEmailFilter } from "../filters/sent-email.filter";
import { MembersFilter } from "../filters/members.filter";
import { OrganizationsFilter } from "../filters/organizations.filter";
import { PropertiesFilter } from "../filters/properties.filter";
import { PaymentHistoryFilter } from "../filters/payment-history.filter";
import { PropertyChargeFilter } from "../filters/property-charge.filter";
import { PropertyChargeSchedulesFilter } from "../filters/property-charge-schedule.filter";
import { UsersFilter } from "../filters/users.filter";
import { SortOption } from "../models/ui/sort-option.model";
import { ClearedTransactionFilterModel } from "../models/ui/cleared-transaction-filter.model";

export class ListConstants {

    //#region Users list
    public static USERS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Name', value: "Name"},
        { label: 'Email', value: "Email" },
        { label: 'Role', value: "MostPermissiveRole" },
        { label: 'Status', value: "Status"},
        { label: 'Last Login', value: "lastLogin" },
        { label: 'Invitation Status', value: "invitationStatus" }
      ];

    public static USERS_FILTER_DEFAULT = (): UsersFilter => <UsersFilter>{
        searchTerm: '',
        userIds: [],
        firstNames: [],
        lastNames: [],
        emailAddresses: [],
        phoneNumbers: [],
        statuses: [UserStatuses.Active] as UserStatuses[],
        invitationStatuses: [] as UserInvitationStatuses[],
        roleIds: [],
        hasRole: undefined
      };

    public static USERS_ORDERBY_DEFAULT = (): UserListSortOptions => UserListSortOptions.LastLoginDESC;
    //#endregion

    //#region Staff Management list
    public static STAFF_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Name', value: "Name" },
        { label: 'Role', value: "MostPermissiveRole" },
        { label: 'Status', value: "Status" },
        { label: 'Last Login', value: "LastLogin" }
      ];

    public static STAFF_ORDERBY_DEFAULT = (): UserListSortOptions => UserListSortOptions.NameASC;
    //#endregion

    //#region Organizations list
    public static ORGANIZATIONS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Organization Name', value: "Name" },
        { label: 'Billing Status', value: "BillingStatus" },
        { label: 'Organization Status', value: "OrganizationStatus" },
        { label: 'Pricing Tier', value: "PricingTier" },
        { label: 'Members', value: "MemberCount"}
    ];
    
    public static ORGANIZATIONS_FILTER_DEFAULT = (): OrganizationsFilter => <OrganizationsFilter>{
        searchTerm: '',
        organizationIds: [],
        names: [],
        billingStatuses: [] as BillingStatuses[],
        pricingTiers: [] as PricingTiers[],
        statuses: [OrganizationStatuses.Active] as OrganizationStatuses[], 
      };

    public static ORGANIZATIONS_ORDERBY_DEFAULT = (): OrganizationListSortOptions => OrganizationListSortOptions.OrganizationNameASC;
    //#endregion

    //#region Members list
    public static MEMBERS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Name', value: "Name"},
        { label: 'Email', value: "Email" },
        { label: 'Phone Number', value: "Phone" },
        { label: 'Role', value: "MostPermissiveRole" },
        { label: 'Status', value: "Status" },
        { label: 'Last Login', value: "LastLogin" }
      ];

    public static MEMBERS_FILTER_DEFAULT = (): MembersFilter => <MembersFilter>{
        searchTerm: '',
        memberIds: [],
        organizationIds: [],
        firstNames: [],
        lastNames: [],
        emailAddresses: [],
        phoneNumbers: [],
        statuses: [MemberStatuses.Created, MemberStatuses.Invited, MemberStatuses.Active] as MemberStatuses[],
        roleIds: [],
        lastLogin: []
    };

    // The email blast targets members who haven't signed up yet, so its recipient list also defaults to
    // Expired invites — deliberately separate from the Members page default (DEV-2694).
    // hasEmail is pinned to true so the recipient picker never lists members with no deliverable address (DEV-2761).
    public static MEMBERS_EMAIL_BLAST_FILTER_DEFAULT = (): MembersFilter => <MembersFilter>{
        ...ListConstants.MEMBERS_FILTER_DEFAULT(),
        statuses: [MemberStatuses.Created, MemberStatuses.Invited, MemberStatuses.Expired, MemberStatuses.Active] as MemberStatuses[],
        hasEmail: true
    };

    public static MEMBERS_ORDERBY_DEFAULT = (): string => MemberListSortOptions.NameASC;
    //#endregion  

    //#region Members list
    public static PROPERTIES_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Address', value: "Address"},
        { label: 'Title', value: "Title" },
        { label: 'Lot Number', value: "LotNumber" },
        { label: 'Member Count', value: "MemberCount" },
        { label: 'Status', value: "Status" }
      ];

    public static PROPERTIES_FILTER_DEFAULT = (): PropertiesFilter => <PropertiesFilter>{
        searchTerm: '',
        propertyIds: [],
        organizationIds: [],
        titles: [],
        lotNumbers: [],
        primaryAddresses: [],
        secondaryAddresses: [],
        cities: [],
        stateProvinces: [],
        countries: [],
        primaryZips: [],
        minimumMemberCount: undefined,
        maximumMemberCount: undefined,
        addressTypes: [] as AddressTypes[],
        statuses: [PropertyStatuses.Active] as PropertyStatuses[],
        memberIds: []
    };

    public static PROPERTIES_ORDERBY_DEFAULT = (): string => PropertyListSortOptions.AddressASC;
    //#endregion  

    //#region Property Charge Schedule list
    public static PROPERTY_CHARGE_SCHEDULES_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Name', value: "Name"},
        { label: 'Property Count', value: "PropertyCount" },
        { label: 'Scheduled Amount', value: "ScheduledAmount" },
        { label: 'Frequency', value: "Frequency" },
        { label: 'Last Modified', value: "LastModified" },
        { label: 'Status', value: "Status" }
      ];

    public static PROPERTY_CHARGE_SCHEDULES_FILTER_DEFAULT = (): PropertyChargeSchedulesFilter => ({
        timeZoneId: undefined,
        searchTerm: '',
        propertyChargeScheduleIds: [],
        organizationIds: [],
        propertyIds: [],
        names: [],
        titles: [],
        minimumLastModifiedDate: undefined,
        maximumLastModifiedDate: undefined,
        minimumScheduledAmount: undefined,
        maximumScheduledAmount: undefined,
        frequencies: [],
        statuses: [],
        lotNumbers: [],
        primaryAddresses: []
    } as PropertyChargeSchedulesFilter);

    public static PROPERTY_CHARGE_SCHEDULES_ORDERBY_DEFAULT = (): string => PropertyChargeScheduleListSortOptions.NameAsc;
    //#endregion

    public static COUNTRIES_ORDERBY_DEFAULT = (): string => CountryListSortOptions.NameAsc;

    public static STATE_PROVINCES_ORDERBY_DEFAULT = (): string => StateProvinceListSortOptions.NameAsc;

    //#region Property Charges list
    public static PROPERTY_CHARGES_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Invoice #', value: "PropertyChargeId" },
        { label: 'Property Title', value: "PropertyTitle" },
        { label: 'Address', value: "PrimaryAddress" },
        { label: 'Lot #', value: "LotNumber" },
        { label: 'Issued Date', value: "Issued" },
        { label: 'Due Date', value: "Due" },
        { label: 'Status', value: "Status" },
        { label: 'Owed Amount', value: "OwedAmount" },
        { label: 'Paid Amount', value: "PaidAmount" },
        { label: 'Paid Date', value: "PaidDate" },
        { label: 'Fees', value: "LateFees" }
      ];

    public static PROPERTY_CHARGES_FILTER_DEFAULT = (): PropertyChargeFilter => ({
        timeZoneId: undefined,
        searchTerm: '',
        propertyChargeIds: [],
        propertyChargeScheduleIds: [],
        propertyIds: [],
        organizationIds: [],
        memberIds: [],
        names: [],
        titles: [],
        lotNumbers: [],
        primaryAddresses: [],
        statuses: [ChargeStatus.Late, ChargeStatus.Locked, ChargeStatus.Open, ChargeStatus.Paid, ChargeStatus.PaidLate, ChargeStatus.Pending] as ChargeStatus[],
        minimumIssuanceDate: undefined,
        maximumIssuanceDate: undefined,
        minimumDueDate: undefined,
        maximumDueDate: undefined,
        minimumAmountOwed: undefined,
        maximumAmountOwed: undefined,
        minimumAmountPaid: undefined,
        maximumAmountPaid: undefined,
        minimumLateFees: undefined,
        maximumLateFees: undefined,
        minimumPaidDate: undefined,
        maximumPaidDate: undefined
    } as PropertyChargeFilter);

    public static PROPERTY_CHARGES_INVOICE_DASHBOARD_FILTER_DEFAULT = (): PropertyChargeFilter => ({
        ...ListConstants.PROPERTY_CHARGES_FILTER_DEFAULT(),
        statuses: [ChargeStatus.Open, ChargeStatus.Late, ChargeStatus.Pending] as ChargeStatus[]
    } as PropertyChargeFilter);

    public static PROPERTY_CHARGES_ORDERBY_DEFAULT = (): string => PropertyChargeListSortOptions.PropertyChargeIdDesc;
    //#endregion

    //#region Payment History list
    public static PAYMENT_HISTORY_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Status', value: "Status" },
        { label: 'Date Paid', value: "DatePaid" },
        { label: 'Due Date', value: "DueDate" },
        { label: 'Amount', value: "Amount" },
        { label: 'Fees', value: "Fees" }
    ];

    public static PAYMENT_HISTORY_FILTER_DEFAULT = (): PaymentHistoryFilter => ({
        searchTerm: '',
        timeZoneId: undefined,
        organizationIds: [],
        memberIds: [],
        propertyIds: [],
        propertyChargeIds: [],
        names: [],
        propertyTitles: [],
        primaryAddresses: [],
        lotNumbers: [],
        statuses: [] as PaymentStatus[],
        minimumPaidDate: undefined,
        maximumPaidDate: undefined,
        minimumDueDate: undefined,
        maximumDueDate: undefined,
        minimumAmount: undefined,
        maximumAmount: undefined,
        minimumFees: undefined,
        maximumFees: undefined,
        isManualPayment: undefined
    } as PaymentHistoryFilter);

    public static PAYMENT_HISTORY_ORDERBY_DEFAULT = (): string => PaymentHistoryListSortOptions.DatePaidDesc;
    public static PAYMENT_HISTORY_ORG_VIEW_ORDERBY_DEFAULT = (): string => PaymentHistoryListSortOptions.DatePaidDesc;
    //#endregion

    //#region Expenses list
    public static EXPENSES_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Vendor', value: "VendorName" },
        { label: 'Reference #', value: "ReferenceNumber" },
        { label: 'Expense Account', value: "ExpenseAccount" },
        { label: 'Amount', value: "Amount" },
        { label: 'Date Paid', value: "DatePaid" },
        { label: 'Status', value: "Status" }
    ];

    public static EXPENSES_FILTER_DEFAULT = (): ExpenseFilter => ({
        timeZoneId: undefined,
        organizationIds: [],
        vendorNames: [],
        referenceNumbers: [],
        expenseAccount: undefined,
        minimumAmount: undefined,
        maximumAmount: undefined,
        minimumDatePaid: undefined,
        maximumDatePaid: undefined,
        statuses: []
    } as ExpenseFilter);

    public static EXPENSES_ORDERBY_DEFAULT = (): string => ExpenseListSortOptions.DatePaidDesc;
    //#endregion

    //#region Budgets list
    public static BUDGETS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Name', value: "Name" },
        { label: 'Year', value: "Year" },
        { label: 'Frequency', value: "Frequency" },
        { label: 'Status', value: "Status" },
        { label: 'Modified', value: "LastModified" }
    ];

    public static BUDGETS_FILTER_DEFAULT = (): BudgetsFilter => ({
        includeDeletedBudgets: false,
        searchTerm: '',
        budgetIds: [],
        organizationIds: [],
        names: [],
        statuses: [],
        minimumYear: undefined,
        maximumYear: undefined
    } as BudgetsFilter);

    public static BUDGETS_ORDERBY_DEFAULT = (): string => BudgetListSortOptions.YearDesc;
    //#endregion

    //#region Chart of Accounts list
    public static CHART_OF_ACCOUNTS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Account #', value: "AccountNumber" },
        { label: 'Name', value: "Name" },
        { label: 'Status', value: "Status" }
    ];

    public static CHART_OF_ACCOUNTS_FILTER_DEFAULT = (): ChartOfAccountsFilter => ({
        organizationId: 0,
        categories: [],
        isActive: true,
        searchTerm: ''
    } as ChartOfAccountsFilter);

    public static CHART_OF_ACCOUNTS_ORDERBY_DEFAULT = (): string => ChartOfAccountListSortOptions.AccountNumberAsc;

    // Account numbers must fall within their category's band and are validated client-side as a UX nicety;
    // the server remains authoritative (409 duplicate / 422 out-of-range).
    public static CHART_OF_ACCOUNT_NUMBER_RANGES = (): Record<ChartOfAccountCategory, { min: number; max: number }> => ({
        [ChartOfAccountCategory.Assets]: { min: 1000, max: 1999 },
        [ChartOfAccountCategory.AssetsCash]: { min: 1000, max: 1999 },
        [ChartOfAccountCategory.Liabilities]: { min: 2000, max: 2999 },
        [ChartOfAccountCategory.Equity]: { min: 3000, max: 3999 },
        [ChartOfAccountCategory.Revenue]: { min: 4000, max: 4999 },
        [ChartOfAccountCategory.Expenses]: { min: 5000, max: 5999 }
    });
    //#endregion

    //#region Sent Emails list
    public static SENT_EMAILS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Recipient', value: "Recipient" },
        { label: 'Subject', value: "Subject" },
        { label: 'Type', value: "EmailType" },
        { label: 'Sent', value: "SentAt" },
        { label: 'Status', value: "Status" }
    ];

    public static SENT_EMAILS_FILTER_DEFAULT = (): SentEmailFilter => ({
        timeZoneId: undefined,
        organizationIds: [],
        searchTerm: '',
        recipientEmailAddresses: [],
        recipientUserIds: [],
        recipientMemberIds: [],
        emailTypes: [],
        statuses: [],
        minimumSentDate: undefined,
        maximumSentDate: undefined
    } as SentEmailFilter);

    public static SENT_EMAILS_ORDERBY_DEFAULT = (): string => SentEmailSortOptions.SentAtDesc;
    //#endregion

    //#region Cleared Bank Transactions

    // searchTerm is absent on purpose: the search box sits outside the Filter dropdown, so Reset must
    // leave it alone. Fields the default does not name are preserved by the base class automatically.
    public static CLEARED_TRANSACTIONS_FILTER_DEFAULT = (): ClearedTransactionFilterModel => ({
        dateFrom: undefined,
        dateTo: undefined,
        amountFrom: undefined,
        amountTo: undefined,
        matchType: undefined
    });
    //#endregion

    //#region Feature flags
    public static FEATURE_FLAGS_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Key', value: "Key" },
        { label: 'Modified', value: "LastModified" }
    ];

    public static FEATURE_FLAGS_FILTER_DEFAULT = (): FeatureFlagsFilter => ({
        searchTerm: ''
    } as FeatureFlagsFilter);

    public static FEATURE_FLAGS_ORDERBY_DEFAULT = (): string => FeatureFlagSortOptions.KeyAsc;

    public static FEATURE_FLAG_OVERRIDES_SORT_OPTIONS = (): SortOption[] => [
        { label: 'Organization', value: "OrganizationName" },
        { label: 'Flag', value: "FeatureFlagKey" },
        { label: 'Modified', value: "LastModified" }
    ];

    public static FEATURE_FLAG_OVERRIDES_FILTER_DEFAULT = (): FeatureFlagOverridesFilter => ({
        featureFlagIds: [],
        organizationIds: []
    } as FeatureFlagOverridesFilter);

    public static FEATURE_FLAG_OVERRIDES_ORDERBY_DEFAULT = (): string => FeatureFlagOverrideSortOptions.OrganizationNameAsc;
    //#endregion
}
